import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";

const LocationFormDialog = ({
  open,
  onClose,
  onSave,
  location = null,
  employees = [],
  loading = false,
}) => {
  const isEdit = Boolean(location);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          locationId: location.locationId,
          medicalCenter: location.medicalCenter || "",
          clinical: location.clinical ?? true,
          managerId: location.managerId || "",
          addressOffice: location.addressOffice || "",
          officeEmail: location.officeEmail || "",
          latitude: location.latitude ?? "",
          longitude: location.longitude ?? "",
        });
      } else {
        setForm({
          medicalCenter: "",
          clinical: true,
          managerId: "",
          addressOffice: "",
          officeEmail: "",
          latitude: "",
          longitude: "",
        });
      }
      setError("");
    }
  }, [open, location, isEdit]);

  const handleChange = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleSubmit = () => {
    if (!form.medicalCenter?.trim())
      return setError("Medical center name is required");

    const data = { ...form };
    if (data.managerId) data.managerId = Number(data.managerId);
    else data.managerId = null;
    if (data.latitude !== "") data.latitude = Number(data.latitude);
    else data.latitude = null;
    if (data.longitude !== "") data.longitude = Number(data.longitude);
    else data.longitude = null;
    if (!data.officeEmail) data.officeEmail = null;
    if (!data.addressOffice) data.addressOffice = null;

    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Location" : "New Location"}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={12}>
            <TextField
              label="Medical Center Name *"
              value={form.medicalCenter || ""}
              onChange={handleChange("medicalCenter")}
              fullWidth
              size="small"
            />
          </Grid>
          {isEdit && (
            <Grid size={12}>
              <TextField
                label="Center Key"
                value={location?.centerKey || ""}
                fullWidth
                size="small"
                disabled
                helperText="Auto-generated, cannot be changed"
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Manager</InputLabel>
              <Select
                value={form.managerId || ""}
                label="Manager"
                onChange={handleChange("managerId")}
              >
                <MenuItem value="">None</MenuItem>
                {employees
                  .filter((e) => e.status === 1)
                  .map((e) => (
                    <MenuItem key={e.employeeId} value={e.employeeId}>
                      {e.firstName} {e.lastName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Office Email"
              value={form.officeEmail || ""}
              onChange={handleChange("officeEmail")}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Address"
              value={form.addressOffice || ""}
              onChange={handleChange("addressOffice")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Latitude"
              value={form.latitude ?? ""}
              onChange={handleChange("latitude")}
              fullWidth
              size="small"
              type="number"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Longitude"
              value={form.longitude ?? ""}
              onChange={handleChange("longitude")}
              fullWidth
              size="small"
              type="number"
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.clinical ?? true}
                  onChange={handleChange("clinical")}
                />
              }
              label="Clinical Location"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationFormDialog;
